import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Receipt, Sparkles, ArrowRight, LogOut, Calendar, CheckCircle2, Clock, AlertCircle,
  Download, User, Loader2, ShoppingBag, TrendingUp, LayoutDashboard, ShoppingCart, Gift,
  HelpCircle, Sun, Moon, Bell, Copy, Share2, ChevronDown, Mail, MessageSquare, Send,
  Compass, ExternalLink, Lock, History, Trophy, Activity, Play, Video,
} from 'lucide-react';
import OnboardingTour from '../components/OnboardingTour';
import UpcomingWorkshopsSection from '../components/UpcomingWorkshopsSection';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/user-auth`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_61ca5ff4-bcc2-4cdc-8453-7407936ace18/artifacts/6nklllpo_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

const ICON_MAP = { FileText, Receipt, Sparkles };

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

const formatRelative = (iso) => {
  if (!iso) return '—';
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - t);
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch { return '—'; }
};

// Stable simulator → route + icon map (matches App.js routes)
const SIMULATOR_META = {
  gst_registration: { route: '/gst/registration', icon: FileText, accent: 'from-teal-500 to-cyan-500' },
  gstr1:            { route: '/gst/gstr1',        icon: Receipt,  accent: 'from-blue-500 to-indigo-500' },
  gstr3b:           { route: '/gst/gstr3b',       icon: Receipt,  accent: 'from-violet-500 to-purple-500' },
  tds_return:       { route: '/income-tax/tds',   icon: FileText, accent: 'from-rose-500 to-pink-500' },
  finance_ratios:   { route: '/finance/ratios',   icon: Activity, accent: 'from-emerald-500 to-teal-500' },
};

const statusConfig = {
  active: { label: 'Active', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', icon: CheckCircle2 },
  pending: { label: 'Pending Verification', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30', icon: Clock },
  expired: { label: 'Expired / Rejected', color: 'bg-rose-500/15 text-rose-400 border-rose-500/30', icon: AlertCircle },
};

const navSections = [
  {
    label: 'LEARN',
    items: [
      { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { key: 'history', label: 'Practice History', icon: History },
    ],
  },
  {
    label: 'MY SIMULATORS',
    items: [
      { key: 'purchases', label: 'My Purchases', icon: ShoppingBag },
      { key: 'workshops', label: 'My Workshops', icon: Video },
      { key: 'explore', label: 'Explore Simulators', icon: Compass },
      { key: 'browse', label: 'Browse Plans', icon: ShoppingCart, external: '/' },
    ],
  },
  {
    label: 'ACCOUNT',
    items: [
      { key: 'profile', label: 'Profile', icon: User },
      { key: 'refer', label: 'Refer & Earn', icon: Gift, badge: '₹100' },
      { key: 'help', label: 'Help & Support', icon: HelpCircle },
    ],
  },
];

const UserPortalPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [purchases, setPurchases] = useState([]);
  const [profile, setProfile] = useState({ name: '', phone: '', is_founder: false });
  const [activeNav, setActiveNav] = useState('dashboard');
  const [theme, setTheme] = useState(() => localStorage.getItem('practiq_portal_theme') || 'light');
  const [showUserMenu, setShowUserMenu] = useState(false);
  // Practice History state
  const [historyData, setHistoryData] = useState(null); // {total_events, activities[], by_simulator[]}
  const [historyLoading, setHistoryLoading] = useState(false);

  // Onboarding tour (auto-runs once on first portal visit; manually replayable from Help tab)
  const [tourKey, setTourKey] = useState(0); // bump to force-remount the tour for replay
  const [tourAutoStart, setTourAutoStart] = useState(true);
  const replayTour = () => {
    try { localStorage.removeItem('practiq_portal_tour_done'); } catch (_) { /* ignore */ }
    setActiveNav('dashboard'); // ensure target elements exist
    setTourAutoStart(true);
    setTourKey((k) => k + 1);
  };

  const email = localStorage.getItem('practiq_portal_email') || '';
  const storedName = localStorage.getItem('practiq_portal_name') || '';
  const userName = profile.name || storedName || email.split('@')[0] || 'User';
  const token = localStorage.getItem('practiq_portal_token') || '';

  // Strip honorifics (CA/CS/CMA/Dr/Mr/Mrs/Ms) before deriving display name
  const _nameParts = (userName || 'User').trim().split(/\s+/);
  const _stripped = (_nameParts.length > 1 && /^(CA|CS|CMA|Dr|Mr|Mrs|Ms)\.?$/i.test(_nameParts[0]))
    ? _nameParts.slice(1).join(' ')
    : (userName || 'User');
  const firstName = _stripped.split(/\s+/)[0] || 'User';
  const avatarLetter = (firstName[0] || 'U').toUpperCase();

  // Theme persistence
  useEffect(() => {
    localStorage.setItem('practiq_portal_theme', theme);
  }, [theme]);

  // Auth + data fetch
  useEffect(() => {
    if (!token || !email) {
      navigate('/portal/login');
      return;
    }
    const fetchData = async () => {
      try {
        // Fetch profile + purchases in parallel
        const [meRes, purchasesRes] = await Promise.all([
          fetch(`${API_BASE}/me`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API_BASE}/my-purchases`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (meRes.status === 401 || purchasesRes.status === 401) {
          localStorage.removeItem('practiq_portal_token');
          localStorage.removeItem('practiq_portal_email');
          navigate('/portal/login');
          return;
        }
        const meData = await meRes.json();
        const purchasesData = await purchasesRes.json();
        setProfile({
          name: meData.name || '',
          phone: meData.phone || '',
          is_founder: !!meData.is_founder,
        });
        if (meData.name) localStorage.setItem('practiq_portal_name', meData.name);
        setPurchases(purchasesData.purchases || []);
      } catch {
        toast.error('Could not load portal data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, email, navigate]);

  // Load Practice History (used by Dashboard widget + History tab). Refetch on tab switch.
  useEffect(() => {
    if (!email) return;
    if (activeNav !== 'dashboard' && activeNav !== 'history') return;
    let cancelled = false;
    const load = async () => {
      setHistoryLoading(true);
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/activity/my-activity?email=${encodeURIComponent(email)}`
        );
        if (!res.ok) throw new Error('history fetch failed');
        const data = await res.json();
        if (!cancelled) setHistoryData(data);
      } catch (_) {
        if (!cancelled) setHistoryData({ total_events: 0, activities: [], by_simulator: [] });
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [email, activeNav]);

  const handleLogout = () => {
    localStorage.removeItem('practiq_portal_token');
    localStorage.removeItem('practiq_portal_email');
    toast.success('Signed out');
    navigate('/');
  };

  const handleAccess = (route, status) => {
    if (status !== 'active') {
      toast.error(status === 'pending'
        ? 'Your purchase is pending admin verification. We\'ll email you once activated.'
        : 'This subscription has expired. Please renew.');
      return;
    }
    navigate(route);
  };

  const handleDownloadReceipt = (purchase) => {
    const lines = [
      'PractiQ LLP — Payment Receipt',
      '================================',
      `Email:        ${email}`,
      `Plan:         ${purchase.plan_name}`,
      `Duration:     ${purchase.duration}`,
      `Amount:       Rs. ${purchase.amount}`,
      `UTR Number:   ${purchase.utr_number || '—'}`,
      `Status:       ${statusConfig[purchase.status]?.label || purchase.status}`,
      `Purchased on: ${formatDate(purchase.purchased_at)}`,
      `Valid until:  ${formatDate(purchase.validity_until)}`,
      '',
      'Thank you for choosing PractiQ.',
      'A TaxSetu AI Product · www.taxsetu.ai',
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `practiq-receipt-${purchase.payment_id || Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Derive theme-aware classes (only used inside portal scope)
  const isLight = theme === 'light';
  const cls = useMemo(() => ({
    page: isLight ? 'bg-gray-50 text-gray-900' : 'bg-[#0A0F1C] text-white',
    sidebar: isLight ? 'bg-white border-gray-200' : 'bg-[#0F1626] border-white/5',
    topbar: isLight ? 'bg-white/80 backdrop-blur border-gray-200' : 'bg-[#0F1626]/80 backdrop-blur border-white/5',
    card: isLight ? 'bg-white border-gray-200' : 'bg-[#0F1626] border-white/10',
    mutedText: isLight ? 'text-gray-500' : 'text-gray-500',
    softBg: isLight ? 'bg-gray-100' : 'bg-white/5',
    softBgHover: isLight ? 'hover:bg-gray-100' : 'hover:bg-white/[0.04]',
    border: isLight ? 'border-gray-200' : 'border-white/10',
    navActiveBg: isLight ? 'bg-teal-50' : 'bg-teal-500/10',
    navActiveText: isLight ? 'text-teal-700' : 'text-teal-300',
    navInactiveText: isLight ? 'text-gray-600 hover:text-gray-900' : 'text-gray-400 hover:text-white',
    secLabel: isLight ? 'text-gray-400' : 'text-gray-600',
    headingMuted: isLight ? 'text-gray-500' : 'text-gray-500',
  }), [isLight]);

  const activeCount = purchases.filter(p => p.status === 'active').length;
  const pendingCount = purchases.filter(p => p.status === 'pending').length;
  const totalSpend = purchases.reduce((acc, p) => acc + (p.amount || 0), 0);
  const referralCode = `PRACTIQ-${(email.split('@')[0] || 'NEW').toUpperCase().slice(0, 8)}`;

  if (loading) {
    return (
      <div className={`min-h-screen ${cls.page} flex items-center justify-center`}>
        <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
      </div>
    );
  }

  // ============================ RENDER ============================
  return (
    <div className={`fixed inset-0 ${cls.page} flex overflow-hidden`} data-testid="user-portal-page">
      {/* ===== Sidebar ===== */}
      <aside className={`hidden md:flex md:flex-col w-64 ${cls.sidebar} border-r overflow-y-auto`} data-testid="portal-sidebar">
        {/* Logo block */}
        <div className={`px-5 py-5 border-b ${cls.border}`}>
          <button onClick={() => navigate('/')} className="flex items-center gap-3 w-full">
            <img src={LOGO_URL} alt="PractiQ" className="w-9 h-9 object-contain" />
            <div className="text-left">
              <div className="text-base font-semibold">
                <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">PractiQ</span>
              </div>
              <div className={`text-[10px] ${cls.headingMuted} uppercase tracking-wider`}>EdTech AI Sims</div>
            </div>
          </button>
        </div>

        {/* Nav sections */}
        <nav className="flex-1 px-3 py-4 space-y-6">
          {navSections.map((sec) => (
            <div key={sec.label}>
              <p className={`px-3 text-[10px] font-semibold tracking-widest mb-2 ${cls.secLabel}`}>{sec.label}</p>
              <div className="space-y-0.5">
                {sec.items.map((item) => {
                  const Icon = item.icon;
                  const active = activeNav === item.key;
                  return (
                    <button
                      key={item.key}
                      onClick={() => {
                        if (item.external) { navigate(item.external); return; }
                        setActiveNav(item.key);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        active ? `${cls.navActiveBg} ${cls.navActiveText} font-medium` : cls.navInactiveText
                      }`}
                      data-testid={`nav-${item.key}`}
                      data-tour={`nav-${item.key}`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="flex-1 text-left">{item.label}</span>
                      {item.badge && (
                        <span className="text-[9px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded">
                          {item.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Founder-only admin shortcut */}
          {profile.is_founder && (
            <div>
              <p className={`px-3 text-[10px] font-semibold tracking-widest mb-2 ${cls.secLabel}`}>ADMIN</p>
              <div className="space-y-0.5">
                <button
                  onClick={() => navigate('/admin/workshops')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${cls.navInactiveText}`}
                  data-testid="nav-admin-workshops"
                >
                  <Video className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1 text-left">Workshop Enrollments</span>
                  <span className="text-[9px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded">
                    ADMIN
                  </span>
                </button>
              </div>
            </div>
          )}
        </nav>

        {/* Sign out at bottom */}
        <div className={`p-3 border-t ${cls.border}`}>
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${cls.navInactiveText} ${cls.softBgHover}`}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* ===== Main column ===== */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Topbar */}
        <header className={`${cls.topbar} border-b px-5 py-3 flex items-center justify-between gap-3`} data-testid="portal-topbar">
          {/* Page title (mobile shows logo too) */}
          <div className="flex items-center gap-3 md:gap-2">
            <button onClick={() => navigate('/')} className="md:hidden">
              <img src={LOGO_URL} alt="PractiQ" className="w-8 h-8 object-contain" />
            </button>
            <h2 className="text-lg font-semibold capitalize">
              {activeNav === 'dashboard' ? 'Dashboard' :
                activeNav === 'history' ? 'Practice History' :
                activeNav === 'purchases' ? 'My Purchases' :
                  activeNav === 'workshops' ? 'My Workshops' :
                  activeNav === 'explore' ? 'Explore Simulators' :
                    activeNav === 'profile' ? 'Profile' :
                      activeNav === 'refer' ? 'Refer & Earn' :
                        activeNav === 'help' ? 'Help & Support' : 'Portal'}
            </h2>
          </div>

          <div className="flex items-center gap-1.5">
            {/* Theme toggle */}
            <button
              onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
              className={`p-2 rounded-lg ${cls.softBgHover} ${cls.navInactiveText}`}
              title={`Switch to ${isLight ? 'dark' : 'light'} mode`}
              data-testid="theme-toggle"
            >
              {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </button>

            {/* Notifications */}
            <button
              className={`p-2 rounded-lg ${cls.softBgHover} ${cls.navInactiveText} relative`}
              title="Notifications"
              data-testid="notifications-btn"
              onClick={() => toast('No new notifications', { icon: '🔔' })}
            >
              <Bell className="w-4 h-4" />
            </button>

            {/* User pill */}
            <div className="relative ml-1">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg ${cls.softBg} ${cls.softBgHover}`}
                data-testid="user-menu-trigger"
              >
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-xs font-bold text-white">
                  {avatarLetter}
                </div>
                <span className="text-sm font-medium hidden sm:inline">{firstName}</span>
                <ChevronDown className={`w-3 h-3 ${cls.mutedText}`} />
              </button>
              {showUserMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                  <div className={`absolute right-0 top-full mt-2 w-56 ${cls.card} border rounded-xl shadow-2xl z-50 overflow-hidden`}>
                    <div className={`px-4 py-3 border-b ${cls.border}`}>
                      <p className="text-sm font-semibold truncate">{userName}</p>
                      <p className={`text-[11px] truncate ${cls.mutedText}`}>{email}</p>
                    </div>
                    <button onClick={() => { setActiveNav('profile'); setShowUserMenu(false); }} className={`w-full text-left px-4 py-2.5 text-sm ${cls.softBgHover} flex items-center gap-2`}>
                      <User className="w-4 h-4" /> Profile
                    </button>
                    <button onClick={() => { setActiveNav('help'); setShowUserMenu(false); }} className={`w-full text-left px-4 py-2.5 text-sm ${cls.softBgHover} flex items-center gap-2`}>
                      <HelpCircle className="w-4 h-4" /> Help & Support
                    </button>
                    <div className={`border-t ${cls.border}`}>
                      <button onClick={handleLogout} className={`w-full text-left px-4 py-2.5 text-sm text-rose-400 ${cls.softBgHover} flex items-center gap-2`}>
                        <LogOut className="w-4 h-4" /> Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Scrollable body */}
        <main className="flex-1 overflow-y-auto p-5 lg:p-8">
          {activeNav === 'dashboard' && (
            <DashboardSection
              userName={userName}
              firstName={firstName}
              email={email}
              purchases={purchases}
              activeCount={activeCount}
              pendingCount={pendingCount}
              totalSpend={totalSpend}
              cls={cls}
              onAccess={handleAccess}
              onBrowse={() => navigate('/')}
              onViewAll={() => setActiveNav('purchases')}
              historyData={historyData}
              onViewHistory={() => setActiveNav('history')}
              onOpenRoute={(route) => navigate(route)}
            />
          )}
          {activeNav === 'purchases' && (
            <PurchasesSection
              purchases={purchases}
              cls={cls}
              onAccess={handleAccess}
              onDownload={handleDownloadReceipt}
              onBrowse={() => navigate('/')}
            />
          )}
          {activeNav === 'workshops' && (
            <WorkshopsTabSection cls={cls} email={email} token={token} />
          )}
          {activeNav === 'explore' && (
            <ExploreSection
              purchases={purchases}
              cls={cls}
              isFounder={profile.is_founder}
              onOpen={(route) => navigate(route)}
              onBrowse={() => navigate('/')}
            />
          )}
          {activeNav === 'history' && (
            <HistorySection
              data={historyData}
              loading={historyLoading}
              cls={cls}
              onOpen={(route) => route && navigate(route)}
              onBrowse={() => navigate('/')}
            />
          )}
          {activeNav === 'profile' && (
            <ProfileSection email={email} userName={userName} avatarLetter={avatarLetter} purchases={purchases} totalSpend={totalSpend} activeCount={activeCount} cls={cls} />
          )}
          {activeNav === 'refer' && (
            <ReferSection code={referralCode} cls={cls} />
          )}
          {activeNav === 'help' && (
            <HelpSection cls={cls} email={email} onReplayTour={replayTour} />
          )}
        </main>
      </div>

      {/* Welcome Onboarding Tour — auto-shows once for new portal users, replayable via Help tab */}
      <OnboardingTour
        key={tourKey}
        autoStart={tourAutoStart}
        storageKey="practiq_portal_tour_done"
        onClose={() => setTourAutoStart(false)}
        steps={[
          {
            title: `Welcome to PractiQ, ${firstName || 'there'}!`,
            body:
              'Your hands-on practice hub for GST, TDS, Finance & more. Let me show you around in 4 quick steps — takes under 30 seconds.',
            target: null,
            placement: 'center',
          },
          {
            title: 'Your Dashboard',
            body:
              'Live stats on your purchases, active courses and recent practice sessions all in one place.',
            target: '[data-tour="nav-dashboard"]',
            placement: 'right',
          },
          {
            title: 'Practice History',
            body:
              'Every simulator you touch is tracked here — last practiced date, attempts, best score, and a "Resume" button to jump straight back in.',
            target: '[data-tour="nav-history"]',
            placement: 'right',
          },
          {
            title: 'Explore Simulators',
            body:
              'Browse all available simulators. Start with the FREE GST Registration practice — perfect first session. GSTR-1 / GSTR-3B unlock with a paid plan.',
            target: '[data-tour="nav-explore"]',
            placement: 'right',
          },
          {
            title: "You're all set! 🚀",
            body:
              'Tip: hit the search bar in the navbar to jump to any simulator instantly. You can replay this tour anytime from Help & Support.',
            target: null,
            placement: 'center',
          },
        ]}
      />
    </div>
  );
};

// ============================ SECTIONS ============================
const StatCard = ({ icon: Icon, label, value, color, cls }) => (
  <div className={`${cls.card} border rounded-2xl p-5`}>
    <div className="flex items-center gap-3 mb-2">
      <Icon className={`w-5 h-5 ${color}`} />
      <p className={`text-xs ${cls.mutedText} uppercase tracking-wider`}>{label}</p>
    </div>
    <p className={`text-3xl font-bold ${color}`}>{value}</p>
  </div>
);

const DashboardSection = ({ userName, firstName, email, purchases, activeCount, pendingCount, totalSpend, cls, onAccess, onBrowse, onViewAll, historyData, onViewHistory, onOpenRoute }) => {
  const recent = (historyData?.activities || []).slice(0, 3);
  const sims = historyData?.by_simulator || [];
  const totalAttempts = sims.reduce((s, x) => s + (x.attempts || 0), 0);
  const completedCount = sims.filter(s => s.current_status === 'Completed').length;

  // Free Simulators progress derivation (currently: GST Registration)
  // Map every event "action" to a discrete progress bucket and keep the max
  const freeSims = (() => {
    const ACTION_PROGRESS = { viewed: 15, started: 25, saved: 60, completed: 100, submitted: 100 };
    const buildFor = (simId, fallbackName) => {
      const events = (historyData?.activities || []).filter(ev => ev.simulator_id === simId);
      const simSummary = sims.find(s => s.simulator_id === simId);
      const name = simSummary?.simulator_name || fallbackName;
      const attempts = simSummary?.attempts || 0;
      let progress = 0;
      let last = null;
      events.forEach(ev => {
        const p = ACTION_PROGRESS[(ev.action || '').toLowerCase()] || 0;
        if (p > progress) progress = p;
        if (!last || ev.created_at > last) last = ev.created_at;
      });
      const status = progress >= 100 ? 'Completed' : progress > 0 ? 'In Progress' : 'Not started';
      return { id: simId, name, attempts, progress, last, status };
    };
    return [
      buildFor('gst_registration', 'GST Registration Practice'),
    ];
  })();

  return (
  <div>
    <div className="mb-8">
      <p className={`text-xs ${cls.mutedText} tracking-widest uppercase mb-1`}>My Portal</p>
      <h1 className="text-3xl font-bold">Welcome back, {firstName || userName.split(' ')[0]}<span className="text-teal-400">.</span></h1>
      <p className={`text-sm ${cls.mutedText} mt-1`}>Signed in as <span className="font-medium">{email}</span></p>
    </div>

    {/* Upcoming Live Workshops — top slot to promote paid live sessions */}
    <UpcomingWorkshopsSection variant="themed" cls={cls} />

    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
      <StatCard icon={ShoppingBag} label="Total Purchases" value={purchases.length} color="text-teal-400" cls={cls} />
      <StatCard icon={CheckCircle2} label="Active" value={activeCount} color="text-emerald-400" cls={cls} />
      <StatCard icon={Clock} label="Pending" value={pendingCount} color="text-amber-400" cls={cls} />
    </div>

    {/* Recent Activity widget — only when user has practiced something */}
    {sims.length > 0 && (
      <div className={`${cls.card} border rounded-2xl p-5 mb-8`} data-testid="dashboard-recent-activity">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-teal-400" />
            <h3 className="text-base font-semibold">Recent Practice</h3>
            <span className={`text-xs ${cls.mutedText}`}>· {totalAttempts} session{totalAttempts === 1 ? '' : 's'} across {sims.length} simulator{sims.length === 1 ? '' : 's'}{completedCount > 0 ? ` · ${completedCount} completed` : ''}</span>
          </div>
          <button onClick={onViewHistory} data-testid="dashboard-view-history" className="text-sm text-teal-400 hover:underline">View history →</button>
        </div>
        <div className="space-y-2">
          {recent.map((ev) => (
            <div key={ev.id} className={`flex items-center justify-between px-3 py-2 rounded-lg ${cls.softBg}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded ${
                  ev.action === 'completed' || ev.action === 'submitted'
                    ? 'bg-emerald-500/15 text-emerald-400'
                    : ev.action === 'saved'
                      ? 'bg-amber-500/15 text-amber-400'
                      : 'bg-teal-500/15 text-teal-400'
                }`}>{ev.action}</span>
                <span className="text-sm font-medium truncate">{ev.simulator_name}</span>
              </div>
              <span className={`text-xs ${cls.mutedText} flex-shrink-0 ml-3`}>{formatRelative(ev.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    )}

    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold">Free Simulators</h3>
      <span className={`text-xs ${cls.mutedText}`}>Always free · login required</span>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8" data-testid="free-simulators">
      {freeSims.map((s) => {
        const meta = SIMULATOR_META[s.id] || { route: null, icon: FileText, accent: 'from-teal-500 to-cyan-500' };
        const Icon = meta.icon;
        const statusChip =
          s.status === 'Completed' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
          : s.status === 'In Progress' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
          : 'bg-slate-500/15 text-slate-400 border-slate-500/30';
        return (
          <div
            key={s.id}
            className={`${cls.card} border rounded-2xl p-5 flex flex-col`}
            data-testid={`free-sim-${s.id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${meta.accent} bg-opacity-20 flex items-center justify-center`}>
                <Icon className="w-6 h-6 text-white drop-shadow" />
              </div>
              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[11px]">Free</Badge>
            </div>
            <h4 className="text-base font-semibold mb-1">{s.name}</h4>
            <p className={`text-xs ${cls.mutedText} mb-3`}>
              {s.attempts > 0
                ? `${s.attempts} attempt${s.attempts === 1 ? '' : 's'}${s.last ? ` · Last ${formatRelative(s.last)}` : ''}`
                : 'You haven\u2019t started this yet — perfect first session.'}
            </p>

            {/* Progress bar (mirrors Recent Purchases card style) */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs flex items-center gap-1 text-white/70 dark:text-white/70"><TrendingUp className="w-3 h-3" /> Progress</span>
                <span className="text-xs font-medium">{s.progress}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-teal-400 to-cyan-400 transition-all duration-500"
                  style={{ width: `${s.progress}%` }}
                />
              </div>
            </div>

            <div className="mt-auto flex items-center justify-between gap-2">
              <Badge className={`${statusChip} text-[10px]`}>{s.status}</Badge>
              <Button
                size="sm"
                onClick={() => meta.route && onOpenRoute(meta.route)}
                className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white"
                data-testid={`free-sim-cta-${s.id}`}
              >
                {s.progress > 0 ? 'Resume' : 'Start'} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>

    <div className="flex items-center justify-between mb-4">
      <h3 className="text-lg font-semibold">Recent Purchases</h3>
      {purchases.length > 0 && (
        <button onClick={onViewAll} className="text-sm text-teal-400 hover:underline">View all →</button>
      )}
    </div>

    {purchases.length === 0 ? (
      <div className={`${cls.card} border rounded-2xl p-10 text-center`}>
        <ShoppingBag className={`w-12 h-12 ${cls.mutedText} mx-auto mb-4 opacity-60`} />
        <h3 className="text-lg font-semibold mb-2">No purchases yet</h3>
        <p className={`text-sm ${cls.mutedText} mb-6`}>Explore our simulators and grab a plan that fits you.</p>
        <Button onClick={onBrowse} className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white" data-testid="explore-plans-btn">
          Explore Plans <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {purchases.slice(0, 4).map((p) => (
          <PurchaseCard key={p.payment_id} p={p} cls={cls} onAccess={onAccess} compact />
        ))}
      </div>
    )}

    {/* Quick spend stat */}
    {purchases.length > 0 && (
      <div className={`${cls.card} border rounded-2xl p-5 mt-6 flex items-center justify-between`}>
        <div>
          <p className={`text-xs ${cls.mutedText} uppercase tracking-wider mb-1`}>Lifetime Spend</p>
          <p className="text-2xl font-bold">₹{totalSpend.toLocaleString('en-IN')}</p>
        </div>
        <TrendingUp className="w-8 h-8 text-teal-400 opacity-30" />
      </div>
    )}
  </div>
  );
};

const PurchaseCard = ({ p, cls, onAccess, onDownload, compact = false }) => {
  const Icon = ICON_MAP[p.icon] || FileText;
  const sc = statusConfig[p.status] || statusConfig.pending;
  const StatusIcon = sc.icon;
  return (
    <div className={`${cls.card} border rounded-2xl p-5`} data-testid={`purchase-card-${p.plan}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border border-teal-500/30 flex items-center justify-center">
          <Icon className="w-6 h-6 text-teal-400" />
        </div>
        <Badge className={`${sc.color} text-[11px] gap-1`}>
          <StatusIcon className="w-3 h-3" />
          {sc.label}
        </Badge>
      </div>
      <h3 className="text-base font-semibold mb-1">{p.plan_name}</h3>
      <p className={`text-xs ${cls.mutedText} mb-4`}>{p.plan_subtitle}</p>

      {p.status === 'active' && (
        <div className="mb-4">
          <div className={`flex justify-between text-[11px] ${cls.mutedText} mb-1.5`}>
            <span className="inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Progress</span>
            <span>{p.progress_percent}%</span>
          </div>
          <div className={`w-full h-1.5 ${cls.softBg} rounded-full overflow-hidden`}>
            <div className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 transition-all" style={{ width: `${p.progress_percent}%` }} />
          </div>
        </div>
      )}

      {!compact && (
        <div className="grid grid-cols-2 gap-3 text-xs mb-4">
          <div>
            <p className={`${cls.mutedText} mb-0.5`}>Purchased</p>
            <p className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />{formatDate(p.purchased_at)}</p>
          </div>
          <div>
            <p className={`${cls.mutedText} mb-0.5`}>Valid Until</p>
            <p>{formatDate(p.validity_until)}</p>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          onClick={() => onAccess(p.access_route, p.status)}
          className={`flex-1 ${p.status === 'active' ? 'bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600' : 'bg-gray-500/20 cursor-not-allowed'} text-white`}
          disabled={p.status !== 'active'}
          data-testid={`access-btn-${p.plan}`}
        >
          {p.status === 'active' ? 'Access Now' : sc.label}
          {p.status === 'active' && <ArrowRight className="w-4 h-4 ml-2" />}
        </Button>
        {onDownload && (
          <Button variant="outline" onClick={() => onDownload(p)} className={`${cls.border} ${cls.softBgHover}`} data-testid={`receipt-btn-${p.plan}`} title="Download receipt">
            <Download className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

const PurchasesSection = ({ purchases, cls, onAccess, onDownload, onBrowse }) => (
  <div>
    <div className="mb-6">
      <h1 className="text-2xl font-bold mb-1">My Purchases</h1>
      <p className={`text-sm ${cls.mutedText}`}>All your simulator subscriptions in one place.</p>
    </div>
    {purchases.length === 0 ? (
      <div className={`${cls.card} border rounded-2xl p-10 text-center`}>
        <ShoppingBag className={`w-12 h-12 ${cls.mutedText} mx-auto mb-4 opacity-60`} />
        <h3 className="text-lg font-semibold mb-2">No purchases yet</h3>
        <p className={`text-sm ${cls.mutedText} mb-6`}>Explore our simulators and grab a plan that fits you.</p>
        <Button onClick={onBrowse} className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white">
          Explore Plans <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {purchases.map((p) => (
          <PurchaseCard key={p.payment_id} p={p} cls={cls} onAccess={onAccess} onDownload={onDownload} />
        ))}
      </div>
    )}
  </div>
);

const WorkshopsTabSection = ({ cls, email, token }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!email || !token) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workshops/my-enrollments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        if (!cancelled) setEnrollments(data.enrollments || []);
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [email, token]);

  const statusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return { label: 'Confirmed', className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', Icon: CheckCircle2 };
      case 'rejected':
        return { label: 'Payment Not Verified', className: 'bg-rose-500/15 text-rose-400 border-rose-500/30', Icon: AlertCircle };
      default:
        return { label: 'Pending Verification', className: 'bg-amber-500/15 text-amber-400 border-amber-500/30', Icon: Clock };
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">My Workshops</h1>
        <p className={`text-sm ${cls.mutedText}`}>Live sessions you&apos;ve enrolled in.</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-teal-400" />
        </div>
      ) : enrollments.length === 0 ? (
        <div className={`${cls.card} border rounded-2xl p-10 text-center`}>
          <Video className={`w-12 h-12 ${cls.mutedText} mx-auto mb-4 opacity-60`} />
          <h3 className="text-lg font-semibold mb-2">No workshops enrolled yet</h3>
          <p className={`text-sm ${cls.mutedText} mb-6`}>Join our upcoming live masterclass to get hands-on with GST.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4" data-testid="my-workshops-list">
          {enrollments.map((e) => {
            const s = statusBadge(e.status);
            return (
              <div key={e.enrollment_id} className={`${cls.card} border rounded-2xl p-5`} data-testid={`enrollment-${e.enrollment_id}`}>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="min-w-0">
                    <h4 className="text-base font-semibold mb-1">{e.workshop_title}</h4>
                    <div className={`flex items-center gap-3 text-xs ${cls.mutedText} flex-wrap`}>
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {e.workshop_date_label || formatDate(e.workshop_date_iso)}</span>
                      <span className="flex items-center gap-1"><Video className="w-3.5 h-3.5" /> {e.workshop_platform || 'Zoom'}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {e.workshop_duration || '1.5–2 hrs'}</span>
                    </div>
                  </div>
                  <Badge className={`${s.className} text-[11px] flex items-center gap-1 flex-shrink-0`}>
                    <s.Icon className="w-3 h-3" />
                    {s.label}
                  </Badge>
                </div>

                <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t ${cls.border} text-xs`}>
                  <div>
                    <p className={`${cls.mutedText} mb-0.5`}>Amount Paid</p>
                    <p className="font-semibold">₹{e.amount}</p>
                  </div>
                  <div>
                    <p className={`${cls.mutedText} mb-0.5`}>UTR / Txn ID</p>
                    <p className="font-mono text-[11px] truncate">{e.utr_number}</p>
                  </div>
                  <div>
                    <p className={`${cls.mutedText} mb-0.5`}>Enrolled</p>
                    <p className="font-medium">{formatRelative(e.created_at)}</p>
                  </div>
                  <div>
                    <p className={`${cls.mutedText} mb-0.5`}>Zoom Link</p>
                    <p className="font-medium">
                      {e.join_link ? (
                        <a href={e.join_link} target="_blank" rel="noreferrer" className="text-teal-400 hover:underline">Join →</a>
                      ) : (
                        <span className={cls.mutedText}>Emailed 1 day before</span>
                      )}
                    </p>
                  </div>
                </div>

                {e.status === 'pending_verification' && (
                  <div className="mt-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-200">
                    Our team is verifying your UPI payment. You&apos;ll get the Zoom link on email &amp; WhatsApp within a few hours.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


const ProfileSection = ({ email, userName, avatarLetter, purchases, totalSpend, activeCount, cls }) => (
  <div className="max-w-3xl">
    <div className="mb-6">
      <h1 className="text-2xl font-bold mb-1">Profile</h1>
      <p className={`text-sm ${cls.mutedText}`}>Manage your PractiQ account.</p>
    </div>
    <div className={`${cls.card} border rounded-2xl p-6`}>
      <div className="flex items-center gap-4 mb-6">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-2xl font-bold text-white">
          {avatarLetter}
        </div>
        <div>
          <h3 className="text-lg font-semibold">{userName}</h3>
          <p className={`text-sm ${cls.mutedText}`}>{email}</p>
        </div>
      </div>
      <div className={`space-y-3 border-t ${cls.border} pt-4`}>
        <div className="flex justify-between text-sm">
          <span className={cls.mutedText}>Total purchases</span>
          <span className="font-medium">{purchases.length}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className={cls.mutedText}>Active subscriptions</span>
          <span className="text-emerald-400 font-medium">{activeCount}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className={cls.mutedText}>Lifetime spend</span>
          <span className="font-medium">₹{totalSpend.toLocaleString('en-IN')}</span>
        </div>
      </div>
    </div>
  </div>
);

const ReferSection = ({ code, cls }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    toast.success('Referral code copied!');
  };
  const shareUrl = `https://practiq.taxsetu.ai?ref=${code}`;
  const whatsappShare = `https://wa.me/?text=${encodeURIComponent(`Check out PractiQ — India's tax filing simulator. Use my code ${code} to get a discount! ${shareUrl}`)}`;
  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">
          Refer & Earn <Gift className="w-6 h-6 text-amber-400" />
        </h1>
        <p className={`text-sm ${cls.mutedText}`}>Get ₹100 for every friend who buys a simulator using your code.</p>
      </div>

      <div className={`${cls.card} border rounded-2xl p-6 mb-6 bg-gradient-to-br ${cls.page.includes('gray-50') ? 'from-amber-50 to-orange-50' : 'from-amber-500/[0.04] to-orange-500/[0.04]'}`}>
        <p className={`text-xs ${cls.mutedText} uppercase tracking-wider mb-2`}>Your unique code</p>
        <div className="flex items-center gap-3">
          <div className={`flex-1 ${cls.softBg} px-4 py-3 rounded-xl font-mono text-lg font-semibold border ${cls.border}`} data-testid="referral-code">
            {code}
          </div>
          <Button onClick={handleCopy} className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white" data-testid="copy-code-btn">
            <Copy className="w-4 h-4 mr-2" /> Copy
          </Button>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <a
            href={whatsappShare}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-sm font-medium hover:bg-emerald-500/25 transition-colors"
            data-testid="share-whatsapp"
          >
            <Share2 className="w-4 h-4" /> Share on WhatsApp
          </a>
          <button
            onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('Share link copied!'); }}
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg ${cls.softBg} ${cls.softBgHover} text-sm font-medium`}
            data-testid="copy-link-btn"
          >
            <Copy className="w-4 h-4" /> Copy link
          </button>
        </div>
      </div>

      {/* How it works */}
      <h3 className="text-base font-semibold mb-3">How it works</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { n: 1, t: 'Share your code', d: 'Send your unique code to friends, batchmates, or fellow CA students.' },
          { n: 2, t: 'They purchase', d: 'They use your code at checkout and get 5% off their first plan.' },
          { n: 3, t: 'You earn ₹100', d: 'Cash is credited within 7 days of payment verification.' },
        ].map(s => (
          <div key={s.n} className={`${cls.card} border rounded-2xl p-4`}>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-sm font-bold text-white mb-2">
              {s.n}
            </div>
            <p className="text-sm font-semibold mb-1">{s.t}</p>
            <p className={`text-xs ${cls.mutedText}`}>{s.d}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const ExploreSection = ({ purchases, cls, isFounder, onOpen, onBrowse }) => {
  // All simulators in the catalog
  const ALL = [
    {
      key: 'gstr1',
      name: 'GSTR-1 Filing Simulator',
      subtitle: 'Practice outward supply return filing — replica of the official GST portal',
      icon: FileText,
      color: 'from-teal-500/20 to-cyan-500/20',
      iconColor: 'text-teal-400',
      route: '/gst/gstr1',
      status: 'live',
    },
    {
      key: 'gstr3b',
      name: 'GSTR-3B Filing Simulator',
      subtitle: 'Monthly summary return practice with auto-computed tax tiles',
      icon: FileText,
      color: 'from-cyan-500/20 to-blue-500/20',
      iconColor: 'text-cyan-400',
      route: '/gst/gstr3b',
      status: 'live',
    },
    {
      key: 'tds',
      name: 'TDS Return Filing',
      subtitle: 'Form 24Q, 26Q, 27Q simulation with challan & FVU output',
      icon: Receipt,
      color: 'from-purple-500/20 to-pink-500/20',
      iconColor: 'text-purple-400',
      route: '/income-tax/tds',
      status: 'live',
    },
    {
      key: 'gst-combo',
      name: 'GST Complete Suite',
      subtitle: 'GSTR-1 + GSTR-3B + Registration — full lifecycle practice',
      icon: Sparkles,
      color: 'from-emerald-500/20 to-teal-500/20',
      iconColor: 'text-emerald-400',
      route: '/gst',
      status: 'live',
    },
    {
      key: 'itr',
      name: 'ITR Filing Simulator',
      subtitle: 'ITR-1 to ITR-7 with Form 16 parsing',
      icon: FileText,
      color: 'from-indigo-500/15 to-blue-500/15',
      iconColor: 'text-indigo-400',
      route: '/',
      status: 'soon',
    },
    {
      key: 'finance',
      name: 'Finance Simulator',
      subtitle: 'P&L, Balance Sheet, Cash Flow practice for CA students',
      icon: Sparkles,
      color: 'from-amber-500/15 to-orange-500/15',
      iconColor: 'text-amber-400',
      route: '/',
      status: 'soon',
    },
    {
      key: 'incorporation',
      name: 'Company Incorporation',
      subtitle: 'Pvt Ltd, LLP, OPC — SPICe+, FiLLiP simulation',
      icon: Sparkles,
      color: 'from-rose-500/15 to-fuchsia-500/15',
      iconColor: 'text-rose-400',
      route: '/',
      status: 'soon',
    },
  ];

  const owned = new Set(purchases.filter(p => p.status === 'active').map(p => p.plan));

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">
          Explore Simulators <Compass className="w-6 h-6 text-teal-400" />
        </h1>
        <p className={`text-sm ${cls.mutedText}`}>
          {isFounder
            ? 'Founder access — every simulator is open for you.'
            : 'Browse our catalog. Owned simulators open instantly. Locked ones need a plan.'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ALL.map((s) => {
          const Icon = s.icon;
          const hasAccess = isFounder || owned.has(s.key);
          const isLive = s.status === 'live';
          return (
            <div
              key={s.key}
              className={`${cls.card} border rounded-2xl p-5 flex flex-col`}
              data-testid={`explore-${s.key}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.color} border ${cls.border} flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 ${s.iconColor}`} />
                </div>
                {!isLive ? (
                  <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px]">Coming Soon</Badge>
                ) : hasAccess ? (
                  <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]">
                    {isFounder ? 'Founder' : 'Owned'}
                  </Badge>
                ) : (
                  <Badge className="bg-gray-500/15 text-gray-400 border-gray-500/30 text-[10px] gap-1">
                    <Lock className="w-2.5 h-2.5" /> Locked
                  </Badge>
                )}
              </div>

              <h3 className="text-base font-semibold mb-1">{s.name}</h3>
              <p className={`text-xs ${cls.mutedText} flex-1 mb-4`}>{s.subtitle}</p>

              {!isLive ? (
                <Button disabled className="w-full bg-gray-500/20 cursor-not-allowed text-gray-400">
                  Coming Soon
                </Button>
              ) : hasAccess ? (
                <Button
                  onClick={() => onOpen(s.route)}
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white"
                  data-testid={`explore-open-${s.key}`}
                >
                  Open Simulator <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={onBrowse}
                  variant="outline"
                  className={`w-full ${cls.border} ${cls.softBgHover}`}
                  data-testid={`explore-unlock-${s.key}`}
                >
                  <Lock className="w-4 h-4 mr-2" /> Unlock from ₹499
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const HelpSection = ({ cls, email, onReplayTour }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const handleSend = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error('Please fill in subject and message');
      return;
    }
    setSending(true);
    // For MVP, we just toast — backend integration can be added later
    try {
      await new Promise(r => setTimeout(r, 600));
      toast.success('Message sent! We\'ll reply to your email within 24 hours.');
      setSubject(''); setMessage('');
    } finally {
      setSending(false);
    }
  };

  const faqs = [
    { q: 'How do I access a simulator after purchase?', a: 'Once payment is verified, visit My Purchases and click "Access Now" on the active card.' },
    { q: 'Payment is showing pending. What to do?', a: 'Our admin team typically verifies UTR within 3-4 hours. If it has been longer, write to us.' },
    { q: 'Can I get a refund?', a: 'We follow a 7-day refund policy on unused simulators. See our Refund Policy page for details.' },
    { q: 'How do I update my profile name?', a: 'Profile editing is coming soon. For now, write to support@taxsetu.ai and we will update it.' },
  ];

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">Help & Support <HelpCircle className="w-6 h-6 text-teal-400" /></h1>
        <p className={`text-sm ${cls.mutedText}`}>We&apos;re here to help. Browse FAQs or message us directly.</p>
      </div>

      {/* Replay onboarding tour */}
      {onReplayTour && (
        <div className={`${cls.card} border rounded-2xl p-4 mb-6 flex items-center justify-between gap-3 flex-wrap`} data-testid="replay-tour-row">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 border border-teal-400/30 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-teal-300" />
            </div>
            <div>
              <p className="text-sm font-semibold">Welcome Tour</p>
              <p className={`text-xs ${cls.mutedText}`}>Replay the 4-step intro to your PractiQ portal.</p>
            </div>
          </div>
          <Button
            onClick={onReplayTour}
            className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white"
            data-testid="replay-tour-btn"
          >
            <Play className="w-4 h-4 mr-1.5" /> Replay Tour
          </Button>
        </div>
      )}

      {/* Quick contact pills */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        <a href="mailto:support@taxsetu.ai" className={`${cls.card} border rounded-2xl p-4 flex items-center gap-3 ${cls.softBgHover}`}>
          <Mail className="w-5 h-5 text-teal-400" />
          <div>
            <p className="text-sm font-semibold">Email</p>
            <p className={`text-xs ${cls.mutedText}`}>support@taxsetu.ai</p>
          </div>
        </a>
        <a href="https://wa.me/919999999999" target="_blank" rel="noopener noreferrer" className={`${cls.card} border rounded-2xl p-4 flex items-center gap-3 ${cls.softBgHover}`}>
          <MessageSquare className="w-5 h-5 text-emerald-400" />
          <div>
            <p className="text-sm font-semibold">WhatsApp</p>
            <p className={`text-xs ${cls.mutedText}`}>Mon–Sat · 10am–7pm</p>
          </div>
        </a>
        <a href="/refund-policy" className={`${cls.card} border rounded-2xl p-4 flex items-center gap-3 ${cls.softBgHover}`}>
          <FileText className="w-5 h-5 text-amber-400" />
          <div>
            <p className="text-sm font-semibold">Policies</p>
            <p className={`text-xs ${cls.mutedText}`}>Refund · Terms · Privacy</p>
          </div>
        </a>
      </div>

      {/* FAQs */}
      <h3 className="text-base font-semibold mb-3">Frequently asked</h3>
      <div className="space-y-2 mb-8">
        {faqs.map((f, i) => (
          <details key={i} className={`${cls.card} border rounded-xl group`}>
            <summary className={`cursor-pointer px-4 py-3 text-sm font-medium flex items-center justify-between ${cls.softBgHover}`}>
              <span>{f.q}</span>
              <ChevronDown className={`w-4 h-4 ${cls.mutedText} group-open:rotate-180 transition-transform`} />
            </summary>
            <p className={`px-4 pb-4 text-sm ${cls.mutedText}`}>{f.a}</p>
          </details>
        ))}
      </div>

      {/* Contact form */}
      <h3 className="text-base font-semibold mb-3">Still need help? Send us a message</h3>
      <form onSubmit={handleSend} className={`${cls.card} border rounded-2xl p-5 space-y-4`}>
        <div>
          <label className={`text-xs ${cls.mutedText} block mb-1.5 uppercase tracking-wider`}>From</label>
          <Input value={email} readOnly className={`${cls.softBg} ${cls.border}`} />
        </div>
        <div>
          <label className={`text-xs ${cls.mutedText} block mb-1.5 uppercase tracking-wider`}>Subject</label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="What is this about?" data-testid="help-subject" />
        </div>
        <div>
          <label className={`text-xs ${cls.mutedText} block mb-1.5 uppercase tracking-wider`}>Message</label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Tell us a bit more about what you need help with…" rows={5} data-testid="help-message" />
        </div>
        <Button type="submit" disabled={sending} className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white" data-testid="help-send-btn">
          {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Send message
        </Button>
      </form>
    </div>
  );
};

const HistorySection = ({ data, loading, cls, onOpen, onBrowse }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-7 h-7 animate-spin text-teal-400" />
      </div>
    );
  }

  const sims = data?.by_simulator || [];
  const events = data?.activities || [];
  const totalAttempts = sims.reduce((s, x) => s + (x.attempts || 0), 0);
  const completed = sims.filter(s => s.current_status === 'Completed').length;

  if (sims.length === 0) {
    return (
      <div className={`${cls.card} border rounded-2xl p-10 text-center`}>
        <Trophy className={`w-12 h-12 ${cls.mutedText} mx-auto mb-4 opacity-50`} />
        <h3 className="text-lg font-semibold mb-2">No practice sessions yet</h3>
        <p className={`text-sm ${cls.mutedText} mb-6`}>Pick a simulator and start practising — your progress will show up here automatically.</p>
        <Button onClick={onBrowse} className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white" data-testid="history-browse-btn">
          Browse Simulators <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="history-section">
      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Activity}    label="Total Sessions" value={totalAttempts} color="text-teal-400" cls={cls} />
        <StatCard icon={Trophy}      label="Completed"      value={completed}     color="text-emerald-400" cls={cls} />
        <StatCard icon={LayoutDashboard} label="Simulators Practised" value={sims.length} color="text-blue-400" cls={cls} />
      </div>

      {/* Per-simulator cards */}
      <h3 className="text-lg font-semibold mb-3">Your Simulators</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {sims.map((s) => {
          const meta = SIMULATOR_META[s.simulator_id] || { route: null, icon: FileText, accent: 'from-slate-500 to-slate-700' };
          const Icon = meta.icon;
          const statusColor =
            s.current_status === 'Completed' ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
            : s.current_status === 'In Progress' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30'
            : 'bg-slate-500/15 text-slate-400 border-slate-500/30';
          return (
            <div key={s.simulator_id} className={`${cls.card} border rounded-2xl p-5`} data-testid={`history-sim-card-${s.simulator_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${meta.accent} bg-opacity-20 flex items-center justify-center`}>
                  <Icon className="w-6 h-6 text-white drop-shadow" />
                </div>
                <Badge className={`${statusColor} text-[11px]`}>{s.current_status}</Badge>
              </div>
              <h4 className="text-base font-semibold mb-1">{s.simulator_name}</h4>
              <p className={`text-xs ${cls.mutedText} mb-3`}>
                {s.attempts} attempt{s.attempts === 1 ? '' : 's'}
                {s.best_score != null ? ` · Best ${Math.round(s.best_score)}` : ''}
                {' · '}Last: {formatRelative(s.last_practiced)}
              </p>
              <div className="flex items-center gap-2">
                {meta.route && (
                  <Button
                    size="sm"
                    onClick={() => onOpen(meta.route)}
                    className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white"
                    data-testid={`history-resume-${s.simulator_id}`}
                  >
                    Resume <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Full timeline */}
      <h3 className="text-lg font-semibold mb-3">Activity Timeline</h3>
      <div className={`${cls.card} border rounded-2xl p-5`}>
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="history-timeline">
          {events.map((ev) => (
            <div key={ev.id} className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg ${cls.softBg}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded ${
                  ev.action === 'completed' || ev.action === 'submitted'
                    ? 'bg-emerald-500/15 text-emerald-400'
                    : ev.action === 'saved'
                      ? 'bg-amber-500/15 text-amber-400'
                      : 'bg-teal-500/15 text-teal-400'
                }`}>{ev.action}</span>
                <span className="text-sm font-medium truncate">{ev.simulator_name}</span>
                {ev.score != null && (
                  <span className={`text-xs ${cls.mutedText}`}>· {Math.round(ev.score)} pts</span>
                )}
              </div>
              <span className={`text-xs ${cls.mutedText} flex-shrink-0`}>{formatDate(ev.created_at)} · {formatRelative(ev.created_at)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};


export default UserPortalPage;
